import { AUTOSHIP_RECURRING_ORDERS_DISCOUNT } from '@libs/core';

import '@/components/AutoshipBadge/AutoshipBadge.scss';

export type AutoshipBadgeTextProps = Readonly<{
    discountDisplay?: string;
    isPerSkuSubscribedIndicatorEnabled?: boolean;
    isCustomerSubscribedToTileSku?: boolean;
    isExistingAutoshipSubscriber?: boolean;
}>;

type AutoshipBadgeTextComponentProps = AutoshipBadgeTextProps & {
    icon?: React.ReactNode;
};

export const AutoshipBadgeText = ({
    discountDisplay,
    isPerSkuSubscribedIndicatorEnabled = false,
    isCustomerSubscribedToTileSku = false,
    isExistingAutoshipSubscriber = false,
    icon,
}: AutoshipBadgeTextComponentProps) => {
    // MAP-restricted SKUs omit a usable discountDisplay. It matches PDP condition.
    const hasDiscountDisplay = Boolean(discountDisplay?.trim());

    if (isPerSkuSubscribedIndicatorEnabled && isCustomerSubscribedToTileSku) {
        return <SubscribedWithAutoshipMessaging icon={icon} />;
    }

    if (isExistingAutoshipSubscriber && hasDiscountDisplay) {
        return <RecurringOrdersAutoshipMessaging icon={icon} />;
    }

    if (hasDiscountDisplay && discountDisplay) {
        return <FirstOrderAutoshipDiscountMessaging discountDisplay={discountDisplay} icon={icon} />;
    }

    return <SubscribeWithAutoshipMessaging icon={icon} />;
};

const SubscribedWithAutoshipMessaging = ({ icon }: { icon?: React.ReactNode }) => (
    <AutoshipBadgeTextContainer icon={icon}>
        <span data-testid="autoship-badge-subscribed">Subscribed with </span>
        <span className="autoship-badge__text-brand" data-testid="autoship-badge-brand">
            Autoship
        </span>
    </AutoshipBadgeTextContainer>
);

const RecurringOrdersAutoshipMessaging = ({ icon }: { icon?: React.ReactNode }) => (
    <AutoshipBadgeTextContainer icon={icon}>
        <span className="autoship-badge__discount">{AUTOSHIP_RECURRING_ORDERS_DISCOUNT} off</span> orders with Autoship
    </AutoshipBadgeTextContainer>
);

const FirstOrderAutoshipDiscountMessaging = ({
    discountDisplay,
    icon,
}: {
    discountDisplay: string;
    icon?: React.ReactNode;
}) => (
    <span className="autoship-badge__promo" data-testid="autoship-badge-promo">
        <span className="autoship-badge__discount">{discountDisplay} off</span>
        <span>first order with</span>
        <span className="autoship-badge__brand-lockup" data-testid="autoship-badge-brand-lockup">
            <span className="autoship-badge__text-brand" data-testid="autoship-badge-brand">
                Autoship
            </span>
            {icon}
        </span>
    </span>
);

const SubscribeWithAutoshipMessaging = ({ icon }: { icon?: React.ReactNode }) => (
    <AutoshipBadgeTextContainer icon={icon}>
        Subscribe with{' '}
        <span className="autoship-badge__text-brand" data-testid="autoship-badge-brand">
            Autoship
        </span>
    </AutoshipBadgeTextContainer>
);

const AutoshipBadgeTextContainer = ({ icon, children }: { icon?: React.ReactNode; children: React.ReactNode }) => (
    <>
        <span className="autoship-badge__text">{children}</span>
        {icon}
    </>
);
